import {_, React, observer, observable, computed, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, IDisposer, toJS, Fragment, Popover2, Tooltip2} from '@api'
import {Icon, Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {TabNode} from '@flexlayout'
import {Avatar, Tooltip} from '@material-ui/core'
import {KeyboardArrowRight} from '@material-ui/icons'
import {CatalogItemPreviewMenuItem, CatalogItemRelationshipsContextMenuItem, DetailsNavItemStore, ICatalogItemDetailsPathElement, useStores, useVipLocalStore, utils, VipLocalStore} from '@modules'
import {icons, IWorkspaceForeignKeyDescriptor, StoresInstance, Workspace} from '@stores'
import {FlexLayoutInlineCatalogItemDetailsPaneStore} from '@stores/catalog/flexLayout'
import {ListPreviewCard, MyImage, PrimaryIconButton} from 'centric-ui-components'
import {autorun} from 'mobx'
import {NavigationItem} from 'typedoc-plugin-markdown/dist/navigation-item'
import * as css from './CatalogItemDetailsTabContent.css'

/// region Store

export class CatalogItemDetailsTabContentStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Exclude<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const toDispose: Array<IDisposer> = [
      autorun(() => {
        this.tab.extraData.store = this
      }),
    ]

    return () => toDispose.forEach(d => d())
  }


  @computed get tab() { return this.props.node}

  @computed get inlineTabStore() {
    return this.tab.extraData.store as FlexLayoutInlineCatalogItemDetailsPaneStore
  }

  @computed get workspace() {
    return this.selectedNavStore?.detailsStore?.associatedWorkspace
  }

  @computed get sidebar() { return this.inlineTabStore?.overriddenStores?.sidebar?.right}

  @computed get selectedNavStore() { return this.sidebar?.selectedNavStore}

  @computed get data() { return this.selectedNavStore?.data}

  @computed get dataMap() {
    return {
      ...(this.selectedNavStore?.dataMap ?? {}),
      subtitle2: null,
      subtitle3: null
    }
  }

  get selectedIndex() {
    return this.sidebar.selectedPathIndex
  }

  onCloseNavItemAt = (index: number) => (e) => {
    this.sidebar.path = this.sidebar.path.slice(0, index)
    this.sidebar.selectedPathIndex = index - 1
  }

  @action navItem_onMouseDown = (item: DetailsNavItemStore) => (e: React.MouseEvent) => {
    this.onSelectNavItem(item)(e)
  }

  @action onStartDragNavItem = (item: DetailsNavItemStore) => e => {
    const {tab} = this
    tab.tabStore.onMouseDown(e)
  }

  @action onSelectNavItem = (item: DetailsNavItemStore, removeAnythingAfterInPath = false) => e => {
    const {tab: {selectInTabSet}, catalog: {idFor}, sidebar, sidebar: {path}} = this
    selectInTabSet()
    item.selectNavItem()
    removeAnythingAfterInPath && (sidebar.path = path.slice(0, item.index + 1))
  }

  @action onSelectSpecItem = (pathIndex: number, {value, field, parent, child}: IWorkspaceForeignKeyDescriptor & { value: any }) => e => {
    const {tab: {selectInTabSet}, catalog: {idFor}, sidebar, sidebar: {selectPathItem, path}} = this
    selectInTabSet()

    const id = idFor(value, false),
      pathItem: ICatalogItemDetailsPathElement = {
        where: [{value: id}],
        esIndex: child.catalog.index,
        catalogField: field.field
        //idWithPath: idFor(value)
      }

    sidebar.selectPathItem(pathIndex, pathItem)
    //console.log({value, field, parent, child, path})
  }
}

export const CatalogItemDetailsTabContentStoreContext = React.createContext<CatalogItemDetailsTabContentStore>(null)
export const useCatalogItemDetailsTabContentStore = () => React.useContext(CatalogItemDetailsTabContentStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  node: TabNode
}

/**
 * Some Product -> Some Style -> Some Theme -> Some Color's Detail Page
 * Each individual link can be 'focused' as a hyperlink
 * If the item is a ref list, or there are any other FKs at that level it should be a dropdown allowing choosing any of the linked items
 */
export const CatalogItemDetailsTabContent = observer<MyProps>(function CatalogItemDetailsTabContent({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new CatalogItemDetailsTabContentStore(stores, obsProps)),
    {sidebar, strings} = store

  // console.log({tab, workspace, dataMap})
  useEffect(() => store.registerEffects(), [])

  // console.log({tab, sidebar, selectedNavStore, data})
  // data && console.log(toJS({data, dataMap}, {recurseEverything: true}))
  return (
    <CatalogItemDetailsTabContentStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <div className={css.navItems}>
          {_.isEmpty(sidebar?.navItems)
           ? <div className={css.tabTitle}>
             <Icon className={css.specImage} icon={icons.inlineDetails}/>
             <span className={css.title}>{strings.loading}...</span>
           </div>
           : false ?
             <Fragment>
               {sidebar.selectedPathIndex > 0 && <PrimaryIconButton icon='arrow-left' tooltip='Go Up' onClick={sidebar.navGoUp}/>}
               <FlexLayoutInlineDetailsTabNavItem item={sidebar.selectedNavStore}/>
             </Fragment>
                   : sidebar.navItems.map((nav, i) => (
                <Fragment key={i}>
                  {i > 0 && <KeyboardArrowRight className={css.breadcrumbArrow}/>}
                  <FlexLayoutInlineDetailsTabNavItem item={nav}/>
                </Fragment>))}
        </div>
      </div>
    </CatalogItemDetailsTabContentStoreContext.Provider>)
})

/// endregion

/// region NavItem

export const FlexLayoutInlineDetailsTabNavItem = observer<{ item: DetailsNavItemStore }>(function FlexLayoutInlineDetailsTabNavItem({item}) {
  const {onStartDragNavItem, navItem_onMouseDown, tab, sidebar: {navDetailStores}, stores: {strings, ui: {settings: {detailPaneTabDropdownNav}}}} = useCatalogItemDetailsTabContentStore(),
    {index, selected, title, image, label, workspace, images} = item

  // console.log(toJS({index, selected, title, image}, {recurseEverything: true}))
  return (
    <div className={css.navItem}
         style={...(workspace?.catalog?.cssCardImage ?? {})}
         data-selected={selected}
         data-loaded={navDetailStores[index]?.hasLoadedData === true}
         data-index={index}>
      <div className={css.tabTitle}
      >
        <Popover2
          position='bottom-left'
          interactionKind='hover'
          disabled={index === 0 && !detailPaneTabDropdownNav}
          content={<CatalogItemDetailsTabMenu item={item}/>}
        >
          {!title || !label
           ? <Icon className={css.specImage} icon={icons.inlineDetails}/>
           : !image ? <Avatar className={classNames(css.specImage, css.placeholderAvatar)}>{title && label ? _.first(label) : ''}</Avatar>
                    : <MyImage className={css.specImage} imageUrl={image} imageId={_.first(images)?.exId}/>}
        </Popover2>
        <Tooltip2 content={label}
                  placement='top'
        >
            <span
              data-text={title}
              onMouseDown={navItem_onMouseDown(item)} // To avoid triggering tab drag logic
              //onMouseUp={navDetailStores.length > 1 && utils.eatMouseEvent}
              //onClick={onSelectNavItem(item)}
              onDragStart={onStartDragNavItem(item)}
              className={css.title}>{title ?? `${strings.loading}...`}</span>
        </Tooltip2>
      </div>
    </div>)
})
/// endregion

export const CatalogItemDetailsTabMenu = observer<{ item: DetailsNavItemStore }>(function CatalogItemDetailsTabMenu({item}) {
  const {sidebar, onCloseNavItemAt} = useCatalogItemDetailsTabContentStore()

  if (_.isEmpty(sidebar?.navItems)) {
    // console.log(`sidebar?.navItems is empty`)
    return null
  }

  const {navItems} = sidebar,
    parentNavItem = item.index === 0 ? null : navItems[item.index - 1],
    root: DetailsNavItemStore = _.first(navItems)

  // console.log(foreignKeys.map(v => toJS(v)))

  return (
    <Menu className={css.popoverMenu}>
      {!parentNavItem
       ? <Fragment>
         {/*<MenuDivider title={root.label}/>*/}
         {/*<CatalogItemPreviewMenuItem*/}
         {/*  data={root.data}*/}
         {/*  index={root.workspace.config.catalog.index}*/}
         {/*  workspace={root.workspace.config}*/}
         {/*  shouldDismissPopover={false}*/}
         {/*  onClick={onSelectNavItem(root, true)}*/}
         {/*>*/}
         <ForeignKeyMenuItems
           data={root.data}
           index={item.index + 1}
           workspace={item.workspace}
         />
         {/*</CatalogItemPreviewMenuItem>*/}
       </Fragment>
       : <Fragment>
         <MenuItem icon='clean' text='Close this Level' onClick={onCloseNavItemAt(item.index)}/>
         <ForeignKeyMenuItems
           index={item.index}
           navItem={item}
           workspace={parentNavItem.workspace}
           data={parentNavItem.data}
         />
       </Fragment>}
    </Menu>)
})

export const ForeignKeyMenuItems = observer<{ navItem?: DetailsNavItemStore, data: any, workspace: Workspace, index: number }>(
  function ForeignKeyMenuItems({navItem, data, workspace, index}) {
    const {onSelectSpecItem, sidebar: {navItems}, es, stores: {ui: {settings: {detailPaneTabDropdownNav}}}} = useCatalogItemDetailsTabContentStore(),
      {catalog: {labelForField, idFor}} = workspace,
      navItemAtIndex = navItems[index],
      foreignKeys = (workspace?.foreignKeys ?? []).map(f => ({...f, value: es.readField(data, f.field.field)?.val}))

    // console.log({index, workspace: workspace.id, foreignKeys, data: toJS(data), navItem})
    // console.log(foreignKeys.map(v => toJS(v)))

    return !detailPaneTabDropdownNav ? null : <Fragment>
      {foreignKeys
        .filter(f => f.value && f.child)
        .map((f, i) => {
          const id = idFor(f.value, false),
            {child: {foreignKeys}, value} = f,
            keysWithValues = foreignKeys.map(f => ({...f, value: es.readField(value, f.field.field)?.val})).filter(v => v.value != null)

          return (
            <div key={i}
                 data-id={id}
                 data-field={f.field.field}
                 data-selected={navItemAtIndex?.id == id}
                 className={css.fk}
            >
              <MenuDivider title={labelForField(f.field)}/>
              <CatalogItemPreviewMenuItem
                data={f.value}
                shouldDismissPopover={false}
                index={f.child.catalog.index}
                workspace={f.child.config}
                onClick={onSelectSpecItem(index == 0 ? 1 : index, f)}
              >
                {_.isEmpty(keysWithValues) ? undefined
                                           : <ForeignKeyMenuItems index={index + 1} workspace={f.child} data={f.value}/>
                }
              </CatalogItemPreviewMenuItem>
            </div>)
        })}
    </Fragment>
  })


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/flexLayout-widgets/CatalogItemDetailsTabContent.tsx